"use client";

import { useMemo } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";

import {
  marketDataApi,
  type MarketDataCredentialDto,
  type MarketDataProviderInfo,
} from "@/lib/api";
import { useTenant } from "@/hooks/useTenant";

const FIVE_MINUTES = 5 * 60 * 1000;

/**
 * A credential-capable provider joined with the active org's stored credential
 * (if any). When `credential` is null the org has no BYOD row and falls back to
 * Sovrun's shared credential — `usingSource` reflects that for the UI badge.
 */
export interface ProviderCredentialRow {
  provider: MarketDataProviderInfo;
  credential: MarketDataCredentialDto | null;
  /** Which credential the chart actually uses for this provider right now. */
  usingSource: "org" | "sovrun_default";
}

/**
 * SFWL-005 Phase 2: BYOD market-data credential management for the active org.
 *
 * Joins the public provider registry (`GET /providers`) with the org's masked
 * credentials (`GET /credentials`, org-admin scoped) into one row per
 * credential-requiring provider, and exposes upsert / validate / delete
 * mutations. The query keys include the tenant id so switching subdomains in
 * the same browser flushes the previous org's view.
 */
export function useMarketDataCredentials() {
  const tenant = useTenant();
  const queryClient = useQueryClient();
  const scopeKey = tenant.id ?? "default";

  const providersQuery = useQuery({
    queryKey: ["market-data-providers"],
    queryFn: () => marketDataApi.listProviders(),
    staleTime: FIVE_MINUTES,
  });

  const credentialsQuery = useQuery({
    queryKey: ["market-data-credentials", scopeKey],
    queryFn: () => marketDataApi.listCredentials(),
    staleTime: 30 * 1000,
  });

  const invalidateCredentials = () =>
    queryClient.invalidateQueries({
      queryKey: ["market-data-credentials", scopeKey],
    });

  const upsertMutation = useMutation({
    mutationFn: ({
      providerId,
      payload,
    }: {
      providerId: string;
      payload: Record<string, unknown>;
    }) => marketDataApi.upsertCredential(providerId, payload),
    onSuccess: invalidateCredentials,
  });

  const validateMutation = useMutation({
    mutationFn: (id: string) => marketDataApi.validateCredential(id),
    onSuccess: invalidateCredentials,
  });

  const deleteMutation = useMutation({
    mutationFn: (id: string) => marketDataApi.deleteCredential(id),
    onSuccess: invalidateCredentials,
  });

  const rows = useMemo<ProviderCredentialRow[]>(() => {
    const providers = providersQuery.data?.providers ?? [];
    const credentials = credentialsQuery.data?.credentials ?? [];
    const byProvider = new Map(credentials.map((c) => [c.providerId, c]));

    return providers
      .filter((p) => p.requiresCredential)
      .map((provider) => {
        const credential = byProvider.get(provider.id) ?? null;
        return {
          provider,
          credential,
          usingSource: credential ? "org" : "sovrun_default",
        };
      });
  }, [providersQuery.data, credentialsQuery.data]);

  return {
    rows,
    isLoading: providersQuery.isPending || credentialsQuery.isPending,
    error: (providersQuery.error ?? credentialsQuery.error) as Error | null,
    saveCredential: upsertMutation.mutateAsync,
    validateCredential: validateMutation.mutateAsync,
    deleteCredential: deleteMutation.mutateAsync,
    isSaving: upsertMutation.isPending,
    isValidating: validateMutation.isPending,
    isDeleting: deleteMutation.isPending,
  };
}
